import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Lock, ReceiptText } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { notify, reportLoadError } from '../utils/notify';

// The tax exemptions of the year shown in Tax Config: which kinds of
// earnings, and what share of them, stay out of the Loonbelasting/AOV base.
// Marking one exempt proposes 100 %; an exemption at 0 % is refused. The
// overtime exemption is frozen until the client decides.
const TaxExemptionsSection = ({ year, canEdit }) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [frozen, setFrozen] = useState([]);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const response = await api.get('/settings/tax-exemptions', { params: { year } });
      setFrozen(response.data.frozen || []);
      setRows(response.data.exemptions.map((r) => ({
        ...r,
        exemption_percentage: String(Number(r.exemption_percentage)),
        saved: { is_exempt: r.is_exempt, exemption_percentage: Number(r.exemption_percentage) },
      })));
    } catch (error) {
      reportLoadError(error, t('Error loading tax exemptions'));
    }
  };

  useEffect(() => {
    if (year) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const change = (type, patch) => setRows((prev) => prev.map((r) => (r.earning_type === type ? { ...r, ...patch } : r)));
  const toggle = (r, checked) => change(r.earning_type, {
    is_exempt: checked,
    exemption_percentage: checked && Number(r.exemption_percentage) === 0 ? '100' : r.exemption_percentage,
  });
  const dirty = (r) => r.is_exempt !== r.saved.is_exempt || Number(r.exemption_percentage) !== r.saved.exemption_percentage;

  const save = async (r) => {
    const pct = Number(r.exemption_percentage);
    if (r.exemption_percentage === '' || !Number.isFinite(pct) || pct < 0 || pct > 100) {
      notify(t('The percentage must be between 0 and 100'), 'error');
      return;
    }
    if (r.is_exempt && pct === 0) {
      notify(t('An exempt earning needs a percentage above 0'), 'error');
      return;
    }
    try {
      setSaving(r.earning_type);
      await api.put(`/settings/tax-exemptions/${r.earning_type}`, { config_year: year, is_exempt: r.is_exempt, exemption_percentage: pct });
      notify(t('Exemption saved'), 'success');
      await load();
    } catch (error) {
      notify(error.response?.data?.message || t('Error saving the exemption'), 'error');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="card tc-panel tc-exemptions">
      <h2 className="tc-panel__title"><ReceiptText aria-hidden="true" /> {t('Tax exemptions {year}', { year })}</h2>
      <p className="tc-lead">{t('Earnings marked exempt stay out of the Loonbelasting and AOV base, by the percentage shown.')}</p>
      <div className="tc-table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>{t('Earning')}</th><th>{t('Exempt')}</th><th>{t('Exempt %')}</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isFrozen = frozen.includes(r.earning_type);
              const locked = !canEdit || isFrozen;
              return (
                <tr key={r.earning_type} className={isFrozen ? 'is-frozen' : ''}>
                  <td>
                    <strong>{r.earning_type}</strong>
                    {r.description && <div className="tc-muted">{r.description}</div>}
                    {isFrozen && <div className="tc-frozen"><Lock aria-hidden="true" /> {t("Frozen: pending the client's decision")}</div>}
                  </td>
                  <td>
                    <input type="checkbox" aria-label={t('Exempt {type}', { type: r.earning_type })}
                      checked={r.is_exempt} disabled={locked} onChange={(e) => toggle(r, e.target.checked)} />
                  </td>
                  <td>
                    <input className="input" type="number" min="0" max="100" step="1" aria-label={t('Exempt % of {type}', { type: r.earning_type })}
                      value={r.exemption_percentage} disabled={locked || !r.is_exempt}
                      onChange={(e) => change(r.earning_type, { exemption_percentage: e.target.value })} />
                  </td>
                  <td>
                    {!locked && dirty(r) && (
                      <button type="button" className="btn btn--secondary" disabled={saving === r.earning_type} onClick={() => save(r)}>
                        {t('Save')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaxExemptionsSection;
